import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["enabled_gates"];
// @ts-expect-error not migrated to TS
import { delayedSetupActions } from 'email-management-lib/refactor/actions/general';
// @ts-expect-error not migrated to TS
import AppContainer from 'email-management-lib/refactor/containers/app/AppContainer';
import { ApolloProvider } from '@apollo/client';
import ContentRoutes from 'ContentUtils/Routes';
import { init as initTracking } from 'EmailComponents/utils/tracking';
import { REHYDRATE_FROM_USER_SETTINGS } from 'EmailData/actions/actionTypes';
import { HUA_KEYS_IN_MANAGE, getHubUserAttributesRequest } from 'EmailData/api/hubUserAttributes';
import User from 'EmailData/models/User';
import PortalIdParser from 'PortalIdParser';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { fetchExperiments } from 'email-management-lib/refactor/actions/experiments';
import { parseUserAttributesResponse } from 'email-management-lib/refactor/utils/hubUserAttributes';
import configureStore from 'email-management-lib/refactor/utils/redux/configureStore';
import userSettingsListener from 'email-management-lib/refactor/utils/redux/listeners/userSettingsListener';
import { isValidManageUrl } from 'email-management-lib/refactor/utils/url';
import events from 'email-management-ui/events.yaml';
import setupI18n from 'email-management-ui/setupI18n';
import throttle from 'hs-lodash/throttle';
import userInfo from 'hub-http/userInfo';
import { createApolloClient } from 'hubspot-apollo-client';
import { Map as ImmutableMap } from 'immutable';
import OnboardingTour from 'onboarding-tours/components/OnboardingTour';
import { renderWithNavCopilot } from 'quartz-core/client-with-copilot';
import Raven from 'raven-js';
import { Provider } from 'react-redux';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { BrowserRouter, Route } from 'react-router-dom';
import { REPORTING_TRACKER } from 'reporting-data/constants/usageTracker';
import { hydrateUserInfo } from 'ui-asset-management-lib';
import { createTracker } from 'usage-tracker';
import { setPrimaryTracker } from 'usage-tracker-container';
import rhumbConfig from '../rhumb-config.yaml';
// usage tracker
import { Metrics } from 'email-management-lib/Shared/utils/metrics';
import { init as initMessagingPublishingTracker } from 'messaging-publishing-lib/utils/tracking';
// Copilot context
import CopilotProvider from 'copilot-toolkit/CopilotProvider';
import { Application } from 'foundations-components/components/Application';
import enableCustomPromiseRejectionTracking from './enableCustomPromiseRejectionTracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const apolloClient = createApolloClient();
export default class App {
  async start() {
    if (window.jasmine) return;
    enableCustomPromiseRejectionTracking();
    const bootstrapStart = performance.now();
    try {
      const userInfoStart = performance.now();
      const authConfig = await userInfo().then(hydrateUserInfo);
      Metrics.timer('bootstrap-userInfo').update(performance.now() - userInfoStart);
      const i18nSetupStart = performance.now();
      await setupI18n(authConfig);
      Metrics.timer('bootstrap-i18n').update(performance.now() - i18nSetupStart);
      const user = User.from(authConfig.user);
      this.setupTrackingAndContentRoutes({
        gates: authConfig.gates
      });
      const store = this.setupRedux({
        user,
        gates: authConfig.gates,
        portal: authConfig.portal
      });
      this.render(store);
      store.dispatch(delayedSetupActions(authConfig));
      void this.setupUserSettings(store);
      Metrics.timer('bootstrap').update(performance.now() - bootstrapStart);
    } catch (error) {
      if (error.message !== 'Aborting: redirection in progress' && isValidManageUrl(window.location.pathname, window.location.search)) {
        Raven.captureException(error);
      }
    }
  }
  setupTrackingAndContentRoutes({
    gates
  }) {
    const setupTrackingAndContentRoutesStart = performance.now();
    initTracking(createTracker({
      events,
      properties: {
        namespace: 'email'
      },
      lastKnownEventProperties: ['screen', 'subscreen', 'subscreen2']
    }));
    const trackerInstance = createTracker({
      events
    });
    setPrimaryTracker(trackerInstance, REPORTING_TRACKER);
    initMessagingPublishingTracker(trackerInstance, 'email-management-ui');
    ContentRoutes.init(gates);
    Metrics.timer('bootstrap-setupTrackingAndContentRoutes').update(performance.now() - setupTrackingAndContentRoutesStart);
  }
  async setupUserSettings(store) {
    const setupUserSettingsStart = performance.now();
    const userSettings = await this.getUserSettings();
    store.dispatch({
      type: REHYDRATE_FROM_USER_SETTINGS,
      payload: userSettings.asImmutable()
    });
    const settingsListener = userSettingsListener({
      getCurrentStore: () => store
    });
    const throttledSettingsListener = throttle(settingsListener, 1500, {
      leading: false
    });
    store.subscribe(throttledSettingsListener);
    Metrics.timer('bootstrap-setupUserSettings').update(performance.now() - setupUserSettingsStart);
  }
  async getUserSettings() {
    const userSettingsStart = performance.now();
    let hubUserAttributes = {};
    try {
      hubUserAttributes = await getHubUserAttributesRequest(HUA_KEYS_IN_MANAGE);
    } catch (err) {
      Raven.captureException(err);
      Metrics.timer('bootstrap-userSettingsFetch').update(performance.now() - userSettingsStart);
      return ImmutableMap({}).asMutable();
    }
    const userSettings = parseUserAttributesResponse(hubUserAttributes);
    Metrics.timer('bootstrap-userSettingsFetch').update(performance.now() - userSettingsStart);
    return userSettings.asMutable();
  }
  setupRedux({
    user,
    gates,
    portal
  }) {
    const reduxStart = performance.now();

    // Remove duplicated data we don't want in the store
    const cleanPortal = _objectWithoutPropertiesLoose(portal, _excluded);
    const gatesMap = gates.reduce((gatesObject, gate) => {
      gatesObject[gate] = true;
      return gatesObject;
    }, {});
    const initialState = ImmutableMap({
      user,
      gates: gatesMap,
      portal: cleanPortal
    });
    const store = configureStore(initialState);
    store.dispatch(fetchExperiments());
    Metrics.timer('bootstrap-redux').update(performance.now() - reduxStart);
    return store;
  }
  render(store) {
    const client = new DataFetchingClient();
    const basename = `/email/${PortalIdParser.get()}`;
    const renderStart = performance.now();
    renderWithNavCopilot( /*#__PURE__*/_jsx(Application, {
      name: "email-management-ui",
      children: /*#__PURE__*/_jsx(CopilotProvider, {
        children: /*#__PURE__*/_jsx(ApolloProvider, {
          client: apolloClient,
          children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
            client: client,
            children: /*#__PURE__*/_jsx(Provider, {
              store: store,
              children: /*#__PURE__*/_jsx(BrowserRouter, {
                basename: basename,
                children: /*#__PURE__*/_jsx(Route, {
                  children: ({
                    history
                  }) => /*#__PURE__*/_jsxs(RhumbProvider, {
                    config: rhumbConfig,
                    history: historyAdapter(history),
                    children: [/*#__PURE__*/_jsx(OnboardingTour, {}), /*#__PURE__*/_jsx(AppContainer, {})]
                  })
                })
              })
            })
          })
        })
      })
    }), {
      layoutType: 'panel'
    });
    Metrics.timer('bootstrap-render').update(performance.now() - renderStart);
  }
}